import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import type { RespondedType, ResponseError } from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export default function useChangeGroupMemberRole(groupId: number | null | undefined) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, ChangeGroupUserRoleRequest>({
    mutationKey: ['/ChangeGroupUserRole', groupId],
    mutationFn: (params) =>
      fetcher(
        '/ChangeGroupUserRole',
        {
          GroupID: groupId,
          UserID: params.userId,
          Role: params.role,
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: (_, params) => {
      const queryOptions = generateGroupInfoQueryOptions(groupId);

      queryClient.setQueryData(queryOptions.queryKey, (data) =>
        produce(data, (draft) => {
          if (draft) {
            const member = draft.Members?.find((m) => m.UserID === params.userId);

            if (member) {
              member.Role = params.role;
            }
          }
        }),
      );
    },
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}

type ChangeGroupUserRoleRequest = {
  userId: number;
  role: 0 | 9;
};
